namespace $ {

	export type $shm_codex_api_thread = {
		id: string
		name?: string | null
		preview?: string
		cwd?: string
		modelProvider?: string
		model?: string
		serviceTier?: string | null
		createdAt?: number
		updatedAt?: number
		status?: any
		source?: any
		threadSource?: any
		cliVersion?: string
		managedBy?: string | null
		forkedFromId?: string | null
		lastAgentAt?: number | null
		tags?: string[]
		turns?: $shm_codex_api_turn[]
	}

	export type $shm_codex_api_attachment = {
		id: string
		name: string
		type: string
		data: string
		file?: File
	}

	export type $shm_codex_api_turn = {
		id: string
		status: string
		startedAt?: number | null
		items: $shm_codex_api_item[]
	}

	export type $shm_codex_api_item = {
		id: string
		type: string
		text?: string
		content?: { type: string; text?: string }[]
		summary?: string[]
		command?: string
		aggregatedOutput?: string | null
		status?: string
	}

	export type $shm_codex_api_goal = {
		threadId: string
		objective: string
		status: string
		tokenBudget: number | null
		tokensUsed: number
		timeUsedSeconds: number
	}

	export type $shm_codex_api_queued_message = {
		id: string
		text: string
		images: string[]
		createdAt: number
	}

	export type $shm_codex_api_history_item = {
		id: string
		turn_id: string
		role: 'user' | 'assistant'
		text: string
		created_at: number
	}

	export type $shm_codex_api_history_catalog = {
		items: $shm_codex_api_history_item[]
		next: string | null
	}

	export type $shm_codex_api_history_turn = {
		turn_id: string
		user: $shm_codex_api_history_item | null
		agents: $shm_codex_api_history_item[]
	}

	export type $shm_codex_api_model = {
		id: string
		model?: string
		displayName?: string
		hidden?: boolean
		isDefault?: boolean
		serviceTiers?: { id: string, name: string, description?: string }[]
		defaultServiceTier?: string | null
	}

	export type $shm_codex_api_rate_limit_window = {
		usedPercent: number
		windowDurationMins: number | null
		resetsAt: number | null
	}

	export type $shm_codex_api_rate_limit = {
		limitId: string | null
		limitName: string | null
		primary: $shm_codex_api_rate_limit_window | null
		secondary: $shm_codex_api_rate_limit_window | null
	}

	export type $shm_codex_api_limits = {
		rateLimits: $shm_codex_api_rate_limit
		rateLimitsByLimitId: Record< string, $shm_codex_api_rate_limit > | null
	}

	export type $shm_codex_api_status = {
		ok: boolean
		memory: {
			rss: number
			heapTotal: number
			heapUsed: number
			external: number
			arrayBuffers: number
		}
		uptime: number
	}

	export class $shm_codex_api_error extends Error {
		constructor( public status: number, message: string ) {
			super( message )
		}
	}

	export class $shm_codex_api {
		private static token_providers = new Map< string, ()=> string >()
		private static history_legacy_cache = new Map< string, readonly $shm_codex_api_history_item[] >()

		static token_provider( base: string, next?: ( ()=> string ) | null ) {
			const origin = new URL( base, $mol_dom_context.document?.location.href ).origin
			if( next === null ) this.token_providers.delete( origin )
			else if( next !== undefined ) this.token_providers.set( origin, next )
			return this.token_providers.get( origin )
		}

		static token( base = '' ) {
			if( base ) {
				const provided = String( this.token_provider( base )?.() ?? '' ).trim()
				if( provided ) return provided
			}
			return $shm_agent_token()
		}

		static url( base: string, path: string ) {
			return base.replace( /\/$/, '' ) + path
		}

		static async json( base: string, path: string, init?: RequestInit ) {
			const token = this.token( base )
			const response = await fetch( this.url( base, path ), {
				... init,
				cache: 'no-store',
				headers: {
					'Content-Type': 'application/json',
					...( token ? { 'X-Hitalama-Agent-Token': token } : {} ),
					... init?.headers,
				},
			} )
			if( !response.ok ) {
				const data = await response.json().catch( ()=> ({}) )
				throw new $shm_codex_api_error( response.status, data.error || response.statusText )
			}
			if( response.status === 204 ) return null
			return response.json()
		}

		static sessions( base: string, stamp = 0, name_prefix = '' ) {
			const params = new URLSearchParams({ limit: '200' })
			for( const prefix of name_prefix.split( '\n' ).filter( Boolean ) ) params.append( 'namePrefix', prefix )
			const suffix = `?${ params }`
			return this.json( base, `/sessions${ suffix }` ) as Promise< { data: $shm_codex_api_thread[] } >
		}

		static models( base: string, stamp = 0 ) {
			return this.json( base, '/models' ) as Promise< { data: $shm_codex_api_model[] } >
		}

		static limits( base: string, stamp = 0 ) {
			return this.json( base, '/limits' ) as Promise< $shm_codex_api_limits >
		}

		static status( base: string, stamp = 0 ) {
			return this.json( base, '/status' ) as Promise< $shm_codex_api_status >
		}

		static restart( base: string ) {
			return this.json( base, '/restart', { method: 'POST' } ) as Promise< { restarting: true } >
		}

		static create_session(
			base: string,
			name: string,
			cwd: string,
			instructions = '',
			settings: { model?: string, serviceTier?: string | null } = {},
		) {
			return this.json( base, '/sessions', {
				method: 'POST',
				body: JSON.stringify({ name, cwd, instructions, ... settings }),
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static thread( base: string, id: string, stamp = 0 ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }` ) as Promise< {
				thread: $shm_codex_api_thread
				activeTurnId: string | null
				queue: $shm_codex_api_queued_message[]
			} >
		}

		private static history_legacy_key( base: string, id: string, stamp: number ) {
			return `${ base }\n${ id }\n${ stamp }`
		}

		private static history_legacy_remember( key: string, items: readonly $shm_codex_api_history_item[] ) {
			this.history_legacy_cache.delete( key )
			this.history_legacy_cache.set( key, items )
			while( this.history_legacy_cache.size > 4 ) {
				this.history_legacy_cache.delete( this.history_legacy_cache.keys().next().value! )
			}
		}

		private static history_legacy_turn(
			items: readonly $shm_codex_api_history_item[],
			turn: string,
		): $shm_codex_api_history_turn {
			let user: $shm_codex_api_history_item | null = null
			const agents = [] as $shm_codex_api_history_item[]
			for( const source of items ) {
				const item = { ... source, turn_id: source.turn_id || source.id }
				if( item.role === 'user' ) {
					if( user ) break
					if( item.turn_id === turn || item.id === turn ) user = item
					continue
				}
				if( user ) agents.push({ ... item, turn_id: turn })
			}
			return { turn_id: turn, user, agents }
		}

		static async history_catalog( base: string, id: string, before = '', limit = 40, stamp = 0 ) {
			const query = new URLSearchParams({ limit: String( limit ) })
			if( before ) query.set( 'before', before )
			const data = await this.json(
				base,
				`/sessions/${ encodeURIComponent( id ) }/history?${ query }`,
			) as $shm_codex_api_history_catalog | { items: $shm_codex_api_history_item[] }
			if( 'next' in data ) return data

			this.history_legacy_remember( this.history_legacy_key( base, id, stamp ), data.items )
			const offset = Number( /^legacy:(\d+)$/.exec( before )?.[ 1 ] || 0 )
			const users = data.items.filter( item => item.role === 'user' ).reverse()
				.map( item => ({ ... item, turn_id: item.turn_id || item.id }) )
			return {
				items: users.slice( offset, offset + limit ),
				next: users.length > offset + limit ? `legacy:${ offset + limit }` : null,
			}
		}

		static async history_turn( base: string, id: string, turn: string, stamp = 0 ) {
			const key = this.history_legacy_key( base, id, stamp )
			const cached = this.history_legacy_cache.get( key )
			if( cached ) return this.history_legacy_turn( cached, turn )
			const query = new URLSearchParams({ turn })
			const data = await this.json(
				base,
				`/sessions/${ encodeURIComponent( id ) }/history?${ query }`,
			) as $shm_codex_api_history_turn | { items: $shm_codex_api_history_item[] }
			if( 'turn_id' in data ) return data
			this.history_legacy_remember( key, data.items )
			return this.history_legacy_turn( data.items, turn )
		}

		static file_test_artifact_fallback( path: string ) {
			const normalized = path.replace( /\\/g, '/' )
			const match = /(^|\/)tests\/results\//.exec( normalized )
			if( !match ) return ''
			const index = match.index + match[ 1 ].length
			const prefix = normalized.slice( 0, index )
			const parent = prefix.replace( /\/$/, '' )
			if( parent === 'shm' || parent.endsWith( '/shm' ) ) return ''
			return `${ prefix }shm/${ normalized.slice( index ) }`
		}

		static async file_data( base: string, id: string, path: string ) {
			const token = this.token( base )
			const request = ( source: string )=> fetch( this.url(
				base,
				`/sessions/${ encodeURIComponent( id ) }/files?${ new URLSearchParams({ path: source }) }`,
			), {
				cache: 'no-store',
				headers: token ? { 'X-Hitalama-Agent-Token': token } : {},
			} )
			const fallback = this.file_test_artifact_fallback( path )
			let response = await request( fallback || path )
			if( response.status === 404 && fallback ) response = await request( path )
			if( !response.ok ) {
				const data = await response.json().catch( ()=> ({}) )
				throw new $shm_codex_api_error( response.status, data.error || response.statusText )
			}
			const blob = await response.blob()
			return new Promise< string >( ( done, fail )=> {
				const reader = new FileReader()
				reader.onload = ()=> done( String( reader.result || '' ) )
				reader.onerror = ()=> fail( reader.error || new Error( 'Image reading failed' ) )
				reader.readAsDataURL( blob )
			} )
		}

		static async file_text( base: string, id: string, path: string ) {
			const token = this.token( base )
			const response = await fetch( this.url(
				base,
				`/sessions/${ encodeURIComponent( id ) }/files?${ new URLSearchParams({ path }) }`,
			), {
				cache: 'no-store',
				headers: token ? { 'X-Hitalama-Agent-Token': token } : {},
			} )
			if( !response.ok ) {
				const data = await response.json().catch( ()=> ({}) )
				throw new $shm_codex_api_error( response.status, data.error || response.statusText )
			}
			return response.text()
		}

		static send( base: string, id: string, text: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/messages`, {
				method: 'POST',
				body: JSON.stringify({ text }),
			} )
		}

		static send_with_images(
			base: string,
			id: string,
			text: string,
			images: readonly $shm_codex_api_attachment[],
			mode: 'start' | 'queue' | 'steer' = 'start',
			clientMessageId = '',
			hitalamaContext = '',
		) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/messages`, {
				method: 'POST',
				body: JSON.stringify({
					text, images, mode, clientMessageId,
					...( hitalamaContext ? { additionalContext: {
						'hitalama-map': { kind: 'application', value: hitalamaContext },
					} } : {} ),
				}),
			} )
		}

		static fork( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/fork`, {
				method: 'POST',
				body: '{}',
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static rename( base: string, id: string, name: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/name`, {
				method: 'POST',
				body: JSON.stringify({ name }),
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static tags( base: string, id: string, tags: readonly string[] ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/tags`, {
				method: 'POST',
				body: JSON.stringify({ tags }),
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static model( base: string, id: string, model: string, serviceTier: string | null ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/model`, {
				method: 'POST',
				body: JSON.stringify({ model, serviceTier }),
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static service_tier( base: string, id: string, serviceTier: string | null ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/service-tier`, {
				method: 'POST',
				body: JSON.stringify({ serviceTier }),
			} ) as Promise< { thread: $shm_codex_api_thread } >
		}

		static remove( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }`, { method: 'DELETE' } )
		}

		static stop( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/stop`, {
				method: 'POST',
				body: '{}',
			} )
		}

		static queue_force( base: string, id: string, message: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/queue/${ encodeURIComponent( message ) }/force`, {
				method: 'POST',
				body: '{}',
			} )
		}

		static queue_edit( base: string, id: string, message: string, text: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/queue/${ encodeURIComponent( message ) }`, {
				method: 'PATCH',
				body: JSON.stringify({ text }),
			} ) as Promise< { message: $shm_codex_api_queued_message } >
		}

		static queue_remove( base: string, id: string, message: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/queue/${ encodeURIComponent( message ) }`, {
				method: 'DELETE',
			} )
		}

		static goal( base: string, id: string, stamp = 0 ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal` ) as Promise< {
				goal: $shm_codex_api_goal | null
			} >
		}

		static goal_set( base: string, id: string, objective: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal`, {
				method: 'PUT',
				body: JSON.stringify({ objective }),
			} )
		}

		static goal_clear( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal`, {
				method: 'DELETE',
			} )
		}

		static goal_pause( base: string, id: string ) {
			return this.json( base, `/sessions/${ encodeURIComponent( id ) }/goal`, {
				method: 'PATCH',
				body: JSON.stringify({ status: 'paused' }),
			} )
		}

	}

	export class $shm_codex_api_events extends $mol_object {

		private source: EventSource | null = null

		url() { return '' }
		thread() { return '' }
		prefixes() { return '' }
		file_only() { return false }
		notify() { return ( event: any )=> {} }

		native() {
			if( this.source ) return this.source
			const events = new URL( $shm_codex_api.url( this.url(), '/events' ) )
			const token = $shm_codex_api.token( this.url() )
			if( token ) events.searchParams.set( 'token', token )
			const thread = this.thread()
			if( thread ) events.searchParams.set( 'thread', thread )
			const prefixes = this.prefixes()
			if( prefixes ) events.searchParams.set( 'prefixes', prefixes )
			if( this.file_only() ) events.searchParams.set( 'fileOnly', '1' )
			const source = this.source = new EventSource( events )
			source.onopen = ()=> {
				if( !this.file_only() ) this.notify()({ method: 'transport/open', params: { threadId: thread } })
			}
			source.onerror = ()=> {
				if( !this.file_only() ) this.notify()({ method: 'transport/error', params: { threadId: thread } })
			}
			source.onmessage = event => {
				try {
					const message = JSON.parse( event.data )
					if( this.file_only() && !message.params?.fileWatch ) return
					this.notify()( message )
				} catch( error ) {
					$mol_fail_log( error )
				}
			}
			return source
		}

		override destructor() {
			this.source?.close()
			this.source = null
			super.destructor()
		}

	}

}
